// Canvas video recorder for the cinematic director.
//
// Wraps MediaRecorder over the WebGL canvas's captureStream so a cinematic
// run can be recorded to a downloadable .webm with one click — no screen-
// capture permission, no browser chrome, exactly the rendered frames.
//
// Usage:
//   const rec = createCanvasRecorder(renderer.domElement);
//   rec.start();                 // begins capturing
//   ... run the cinematic ...
//   await rec.stopAndDownload('flythrough');  // stops + saves the file
//
// Notes:
//  - captureStream(fps) pulls frames as the canvas paints, so the recording
//    tracks the real render rate. We request 60 and let the browser cap it.
//  - Codec is negotiated: VP9 → VP8 → default. All land in a .webm container.
//  - The bitrate is set high (24 Mbps) so fast starfields / bloom don't smear
//    into blocky compression — this is for shareable content, not bandwidth.

const CODEC_CANDIDATES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm'
];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  for (const type of CODEC_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

export function isRecordingSupported(canvas) {
  return !!(
    canvas &&
    typeof canvas.captureStream === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

export function createCanvasRecorder(canvas, options = {}) {
  const fps = options.fps || 60;
  const bitsPerSecond = options.bitsPerSecond || 24_000_000;

  let mediaRecorder = null;
  let stream = null;
  let chunks = [];
  let recording = false;
  let startedAt = 0;

  function start() {
    if (recording) return false;
    if (!isRecordingSupported(canvas)) {
      console.warn('[recorder] canvas capture / MediaRecorder unavailable');
      return false;
    }
    chunks = [];
    stream = canvas.captureStream(fps);
    const mimeType = pickMimeType();
    try {
      mediaRecorder = new MediaRecorder(stream, mimeType
        ? { mimeType, bitsPerSecond }
        : { bitsPerSecond });
    } catch (err) {
      console.warn('[recorder] failed to create MediaRecorder', err);
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
      return false;
    }
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    // Timeslice so data flushes periodically — a long run won't sit in one
    // giant un-flushed buffer, and a crash still leaves a partial file.
    mediaRecorder.start(1000);
    recording = true;
    startedAt = (typeof performance !== 'undefined' ? performance.now() : 0);
    return true;
  }

  function buildBlob() {
    const type = (mediaRecorder && mediaRecorder.mimeType) || 'video/webm';
    return new Blob(chunks, { type });
  }

  // Stop recording and resolve with the recorded Blob (or null if nothing
  // was captured). Does not download — the caller decides.
  function stop() {
    return new Promise((resolve) => {
      if (!recording || !mediaRecorder) {
        resolve(null);
        return;
      }
      mediaRecorder.onstop = () => {
        recording = false;
        if (stream) {
          stream.getTracks().forEach((t) => t.stop());
          stream = null;
        }
        resolve(chunks.length ? buildBlob() : null);
      };
      mediaRecorder.stop();
    });
  }

  async function stopAndDownload(baseName = 'cinematic') {
    const blob = await stop();
    if (!blob) return null;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}-${formatStamp()}.webm`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after a tick so the download has a chance to start.
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return blob;
  }

  function elapsedSeconds() {
    if (!recording) return 0;
    const now = (typeof performance !== 'undefined' ? performance.now() : 0);
    return (now - startedAt) / 1000;
  }

  return {
    start,
    stop,
    stopAndDownload,
    isRecording: () => recording,
    elapsedSeconds
  };
}

// Compact filesystem-safe timestamp (YYYYMMDD-HHMMSS) for the filename.
function formatStamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}
